import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { 
  signIn as authSignIn, 
  signUp as authSignUp, 
  signOut as authSignOut,
  resetPassword as authResetPassword,
  verifyRecoveryToken as authVerifyRecoveryToken,
  updatePassword as authUpdatePassword,
  onAuthStateChange 
} from '../lib/auth';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      // Keep a stable `user` reference when the same account is still signed in.
      // Tab focus / token refresh otherwise recreates the object and remounts data hooks.
      setUser((prev) => {
        const nextUser = nextSession?.user ?? null;
        if (prev?.id && nextUser?.id && prev.id === nextUser.id) {
          return prev;
        }
        return nextUser;
      });
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password, metadata) => {
    try {
      const data = await authSignUp(email, password, metadata);
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signIn = async (email, password, rememberMe = true) => {
    try {
      const data = await authSignIn(email, password, rememberMe);
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      await authSignOut();
      setUser(null);
      setSession(null);
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const resetPassword = async (email) => {
    try {
      await authResetPassword(email);
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const verifyRecoveryToken = async (tokenHash) => {
    try {
      await authVerifyRecoveryToken(tokenHash);
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      await authUpdatePassword(newPassword);
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    verifyRecoveryToken,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
